/**
 * Map CM-API Surveillance picker envelopes → CM-UI feature types.
 * Lab spellings (Surveilliance) kept on wire paths only.
 */

import type {
  SurveillanceBet,
  SurveillanceEvent,
  SurveillanceGraph,
  SurveillanceMarket,
  SurveillanceSubGame,
} from '@/features/surveillance/types'

export type CmSurveillanceListEnvelope<T> = {
  status?: number
  message?: string
  value?: boolean
  data?: T[] | null
}

export type CmSurveillanceSubGameRow = { _id: string; name: string }
export type CmSurveillanceEventRow = {
  _id: string
  name: string
  openDate?: string | null
}
export type CmSurveillanceMarketRow = {
  _id: string
  name: string
  marketStartTime?: string | null
  betfairId: string
}

export function isSurveillanceListOk<T>(
  res: CmSurveillanceListEnvelope<T> | null | undefined,
): boolean {
  if (!res) return false
  if (res.value === false) return false
  if (typeof res.status === 'number' && (res.status < 200 || res.status >= 300)) {
    return false
  }
  return Array.isArray(res.data)
}

/** Static fallbacks when CM-API omits `message` or the catch has no Error. */
export const SURVEILLANCE_STATIC_ERRORS = {
  subGames: 'Unable to load sub games. Please try again.',
  events: 'Unable to load events. Please try again.',
  markets: 'Unable to load markets. Please try again.',
  bets: 'Unable to load surveillance bets. Please try again.',
  regenerate: 'Unable to generate graph. Please try again.',
} as const

/**
 * Prefer API `message` from thrown Error; otherwise use static fallback.
 * Always returns a fresh Error suitable for toast / react-query.
 */
export function surveillanceCatchError(
  err: unknown,
  staticFallback: string,
): Error {
  if (err instanceof Error) {
    const apiMsg = err.message.trim()
    if (apiMsg) return new Error(apiMsg)
  }
  if (typeof err === 'string' && err.trim()) {
    return new Error(err.trim())
  }
  return new Error(staticFallback)
}

/**
 * Reject soft-failure envelopes (HTTP 200 + value:false / bad status / missing data).
 * Uses API message when present, else static fallback.
 */
export function assertSurveillanceListOk<T>(
  res: CmSurveillanceListEnvelope<T> | null | undefined,
  staticFallback: string,
): asserts res is CmSurveillanceListEnvelope<T> & { data: T[] } {
  if (!res) {
    throw new Error(staticFallback)
  }
  if (res.value === false) {
    throw new Error(res.message?.trim() || staticFallback)
  }
  if (typeof res.status === 'number' && (res.status < 200 || res.status >= 300)) {
    throw new Error(res.message?.trim() || staticFallback)
  }
  if (!Array.isArray(res.data)) {
    throw new Error(res.message?.trim() || staticFallback)
  }
}

export function mapSurveillanceSubGames(
  res: CmSurveillanceListEnvelope<CmSurveillanceSubGameRow>,
): SurveillanceSubGame[] {
  if (!isSurveillanceListOk(res)) return []
  const out: SurveillanceSubGame[] = []
  for (const row of res.data ?? []) {
    const id = typeof row._id === 'string' ? row._id.trim() : ''
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    if (!id || !name) continue
    out.push({ id, name })
  }
  return out
}

export function mapSurveillanceEvents(
  res: CmSurveillanceListEnvelope<CmSurveillanceEventRow>,
  subGameId: string,
): SurveillanceEvent[] {
  if (!isSurveillanceListOk(res)) return []
  const out: SurveillanceEvent[] = []
  for (const row of res.data ?? []) {
    const id = typeof row._id === 'string' ? row._id.trim() : ''
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    if (!id || !name) continue
    out.push({
      id,
      name,
      openDate: row.openDate?.trim() ? row.openDate : null,
      subGameId,
    })
  }
  return out
}

export function mapSurveillanceMarkets(
  res: CmSurveillanceListEnvelope<CmSurveillanceMarketRow>,
  eventId: string,
): SurveillanceMarket[] {
  if (!isSurveillanceListOk(res)) return []
  const out: SurveillanceMarket[] = []
  for (const row of res.data ?? []) {
    const id = typeof row._id === 'string' ? row._id.trim() : ''
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    const betfairId =
      typeof row.betfairId === 'string' ? row.betfairId.trim() : ''
    if (!id || !name || !betfairId) continue
    out.push({
      id,
      name,
      betfairId,
      eventId,
      marketStartTime: row.marketStartTime?.trim()
        ? row.marketStartTime
        : null,
    })
  }
  return out
}

/** Optional client-side date window (archive UI) until BQ archive API exists. */
export function filterEventsByOpenDate(
  events: SurveillanceEvent[],
  fromDate?: string,
  toDate?: string,
): SurveillanceEvent[] {
  if (!fromDate && !toDate) return events
  const from = fromDate
    ? new Date(`${fromDate}T00:00:00.000Z`).getTime()
    : Number.NEGATIVE_INFINITY
  const to = toDate
    ? new Date(`${toDate}T23:59:59.999Z`).getTime()
    : Number.POSITIVE_INFINITY
  return events.filter((e) => {
    if (!e.openDate) return true
    const t = new Date(e.openDate).getTime()
    return Number.isFinite(t) && t >= from && t <= to
  })
}

/** Wire shape from `POST /v1/bet/getBetsForSurveilliance`. */
export type CmSurveillanceBetPoint = {
  u?: string
  IP?: string
  t?: string
  p?: number
  s?: number
  d?: number
  h?: string
  ap?: number
  as?: number
}

export type CmSurveillanceHorse = {
  n?: string
  i?: string
  b?: CmSurveillanceBetPoint[]
  u?: string
  fu?: string
}

export type CmSurveillanceGraphPayload = {
  m?: {
    c?: string
    e?: string
    m?: string
    mst?: string | null
    wn?: string
  }
  h?: CmSurveillanceHorse[]
}

export type CmGetBetsForSurveillianceResponse = {
  status?: number
  message?: string
  value?: boolean
  data?: CmSurveillanceGraphPayload | [] | null
}

function mapBetSide(raw: string | undefined): 'BACK' | 'LAY' | null {
  const t = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (t === 'BACK' || t === 'LAY') return t
  return null
}

function mapBetPoint(row: CmSurveillanceBetPoint): SurveillanceBet | null {
  const side = mapBetSide(row.t)
  if (!side) return null
  const p = Number(row.p)
  const s = Number(row.s)
  const d = Number(row.d)
  if (!Number.isFinite(p) || !Number.isFinite(s) || !Number.isFinite(d)) return null
  return {
    u: String(row.u ?? '').trim(),
    IP: row.IP?.trim() || undefined,
    t: side,
    p,
    s,
    d,
    h: row.h?.trim() || undefined,
  }
}

/**
 * Map getBets envelope → graph model.
 * Lab/CM return `data: []` when filters incomplete or no match payload.
 */
export function mapGetBetsForSurveilliance(
  res: CmGetBetsForSurveillianceResponse,
):
  | { ok: true; data: SurveillanceGraph }
  | { ok: false; message: string } {
  if (res.value === false) {
    return { ok: false, message: res.message?.trim() || 'Unable to load graph' }
  }
  if (typeof res.status === 'number' && (res.status < 200 || res.status >= 300)) {
    return { ok: false, message: res.message?.trim() || `Request failed (${res.status})` }
  }
  const payload = res.data
  if (!payload || Array.isArray(payload)) {
    return {
      ok: false,
      message: res.message?.trim() || 'No surveillance data for this selection',
    }
  }
  const m = payload.m ?? {}
  const horses = Array.isArray(payload.h) ? payload.h : []
  return {
    ok: true,
    data: {
      m: {
        c: String(m.c ?? ''),
        e: String(m.e ?? ''),
        m: String(m.m ?? ''),
        mst: m.mst?.trim() ? m.mst : '',
        wn: String(m.wn ?? ''),
      },
      h: horses.map((horse) => ({
        n: String(horse.n ?? '').trim() || String(horse.i ?? ''),
        i: String(horse.i ?? '').trim(),
        b: (horse.b ?? [])
          .map(mapBetPoint)
          .filter((b): b is SurveillanceBet => Boolean(b)),
        u: String(horse.u ?? '').trim(),
        fu: String(horse.fu ?? '').trim(),
      })),
    },
  }
}
